import React from 'react';
import { Timer } from 'lucide-react';
import { format } from 'date-fns';

const fmtTime = (m) => {
  const h = Math.floor(m / 60);
  const mm = m % 60;
  return h > 0 ? `${h}h ${mm > 0 ? `${mm}m` : ''}` : `${mm}m`;
};

export const BookingSummary = ({
  step,
  selectedService,
  bedrooms,
  bathrooms,
  isCarpet,
  carpetRooms,
  stainSeverity,
  selectedDate,
  selectedWindow,
  selectedAddons,
  estimatedMinutes,
  priceData,
}) => (
  <div
    className="bg-white rounded-2xl p-5 shadow-sm border border-zinc-100 sticky top-28"
    data-testid="booking-summary"
  >
    <h3 className="text-base font-bold text-zinc-900 font-heading mb-4">Booking Summary</h3>

    <div className="space-y-2.5 text-sm">
      {selectedService && (
        <div className="flex justify-between">
          <span className="text-zinc-500">Service</span>
          <span className="font-semibold text-zinc-900 text-right text-xs">
            {selectedService.name}
          </span>
        </div>
      )}
      {step >= 2 && !isCarpet && (
        <>
          <div className="flex justify-between">
            <span className="text-zinc-500">Beds</span>
            <span className="font-semibold text-zinc-900">{bedrooms}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-zinc-500">Baths</span>
            <span className="font-semibold text-zinc-900">{bathrooms}</span>
          </div>
        </>
      )}
      {step >= 2 && isCarpet && (
        <>
          <div className="flex justify-between">
            <span className="text-zinc-500">Rooms</span>
            <span className="font-semibold text-zinc-900">{carpetRooms}</span>
          </div>
          {stainSeverity !== 'light' && (
            <div className="flex justify-between">
              <span className="text-zinc-500">Stains</span>
              <span className="font-semibold text-amber-600 capitalize text-xs">
                {stainSeverity}
              </span>
            </div>
          )}
        </>
      )}
      {step >= 3 && selectedDate && (
        <div className="flex justify-between">
          <span className="text-zinc-500">Date</span>
          <span className="font-semibold text-zinc-900 text-xs">
            {format(selectedDate, 'MMM d')}
          </span>
        </div>
      )}
      {step >= 3 && selectedWindow && (
        <div className="flex justify-between">
          <span className="text-zinc-500">Time</span>
          <span className="font-semibold text-zinc-900 text-xs">{selectedWindow.label}</span>
        </div>
      )}
      {step >= 4 && selectedAddons.length > 0 && (
        <div className="flex justify-between">
          <span className="text-zinc-500">Add-ons</span>
          <span className="font-semibold text-zinc-900">{selectedAddons.length}</span>
        </div>
      )}

      {selectedService && (
        <div className="border-t border-zinc-100 pt-2.5 flex justify-between">
          <span className="text-zinc-500 flex items-center gap-1">
            <Timer className="h-3 w-3" /> Time
          </span>
          <span className="font-semibold text-zinc-900">{fmtTime(estimatedMinutes)}</span>
        </div>
      )}

      <div className="border-t border-zinc-100 pt-2.5">
        {priceData?.requires_estimate ? (
          <div className="text-center py-2">
            <p className="text-xs text-zinc-600 font-medium">Custom estimate needed</p>
            <p className="text-[10px] text-zinc-400">We&apos;ll contact you</p>
          </div>
        ) : priceData ? (
          <>
            <div className="flex justify-between mb-1">
              <span className="text-zinc-500 text-xs">Base</span>
              <span className="font-semibold text-zinc-900 text-xs">
                ${priceData.base_price.toFixed(2)}
              </span>
            </div>
            {priceData.addons_total > 0 && (
              <div className="flex justify-between mb-1">
                <span className="text-zinc-500 text-xs">Add-ons</span>
                <span className="font-semibold text-zinc-900 text-xs">
                  +${priceData.addons_total.toFixed(2)}
                </span>
              </div>
            )}
            <div className="flex justify-between text-base font-bold pt-2 border-t border-zinc-100 mt-1">
              <span className="text-zinc-900">Total</span>
              <span className="text-[#F56F05]">${priceData.total_price.toFixed(2)}</span>
            </div>
            {priceData.deposit_required && (
              <div className="mt-2 p-2 bg-amber-50 rounded-lg border border-amber-200">
                <p className="text-[10px] text-amber-800 font-semibold text-center">
                  25% deposit: ${priceData.deposit_required.toFixed(2)}
                </p>
              </div>
            )}
          </>
        ) : (
          <p className="text-xs text-zinc-400 text-center py-2">Select a service to see pricing</p>
        )}
      </div>
    </div>
  </div>
);

export { fmtTime };
